import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  PlusIcon,
  CheckIcon,
  XIcon,
  TrashIcon,
} from "lucide-react";
import { db } from "../../config/firebaseConfig";
import { ref, set, get } from "firebase/database";
import { useToast } from "../../@/components/ui/use-toast";
import SortupDialog from "../../components/common/SortupDialog";
import LayoutSkeleton from "../../components/skeletons/LayoutSkeleton";

interface PrivacyPolicySection {
  title: string;
  content: string;
  listItems?: string[];
}

interface PrivacyPolicyData {
  introduction: string;
  sections: PrivacyPolicySection[];
}

const EditPrivacyPolicy: React.FC = () => {
  // State for storing privacy policy data
  const [privacyData, setPrivacyData] = useState<PrivacyPolicyData>({
    introduction: "",
    sections: [],
  });

  // State for editing introduction
  const [isEditingIntro, setIsEditingIntro] = useState(false);
  const [editIntroText, setEditIntroText] = useState("");

  // State for editing section
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(
    null
  );
  const [editSectionTitle, setEditSectionTitle] = useState("");
  const [editSectionContent, setEditSectionContent] = useState("");

  // State for editing list items
  const [editingListItems, setEditingListItems] = useState<string[]>([]);

  // State for adding new section
  const [isAddingSection, setIsAddingSection] = useState(false);

  // State for adding new list item
  const [newListItem, setNewListItem] = useState("");

  // Loading state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<number | null>(null);

  useEffect(() => {
    // Fetch privacy policy data from Firebase
    const fetchPrivacyData = async () => {
      try {
        const privacyRef = ref(db, "LayoutSettingData/PrivacyPolicy");
        const snapshot = await get(privacyRef);

        if (snapshot.exists()) {
          setPrivacyData(snapshot.val());
        } else {
          // If no data exists, use default content
          const defaultData: PrivacyPolicyData = {
            introduction:
              "At Afrocart, we are committed to protecting your personal information and ensuring your experience with us is safe and secure. This Privacy Policy outlines how we collect, use, and protect your personal information when you visit our website or use our services",
            sections: [
              {
                title: "Information We collect",
                content:
                  "We collect personal information that you provide to us directly when you create an account, place an order, sign up for our newsletter, or interact with our customer support. This information may include your name, email address, phone number, payment information, and delivery address. We also collect information automatically through cookies and similar technologies, such as your IP address, browsing behavior, and device information.",
              },
              {
                title: "Google Calendar Access",
                content:
                  "If you choose to connect your Google Calendar to Afrocart, we will request permission to:",
                listItems: [
                  "Add new events to your Google Calendar.",
                  "Edit existing events based on your requests.",
                  "Retrieve your calendar events to display or synchronize with our platform.",
                  "Update events to reflect changes in scheduling.",
                ],
              },
              {
                title: "How We Use Your Information",
                content: "We use the information we collect to:",
                listItems: [
                  "Process and fulfill your orders",
                  "Provide customer support",
                  "Improve and personalize your shopping experience",
                  "Send you updates, promotions, and relevant news (if you have opted in)",
                  "Analyze and improve our website and services",
                ],
              },
              {
                title: "How We Protect Your Information",
                content:
                  "We take the security of your personal information seriously. We use industry-standard security measures, including encryption and secure servers, to protect your data from unauthorized access, alteration, disclosure, or destruction.",
              },
              {
                title: "Sharing Your Information",
                content:
                  "We do not sell your personal information to third parties. We may share your information with trusted third-party service providers who assist us in operating our website, processing payments, and delivering your orders. These partners are required to maintain the confidentiality of your information and are prohibited from using it for any other purpose.",
              },
              {
                title: "Your Rights",
                content:
                  "You have the right to access, correct, or delete your personal information at any time. If you would like to update your information or request the deletion of your account, please contact our customer service team.",
              },
              {
                title: "Cookies And Tracking Technologies",
                content:
                  "Our website uses cookies to enhance your shopping experience and provide personalized content. You can choose to disable cookies through your browser settings, but this may affect your ability to use certain features of our website.",
              },
              {
                title: "Updating Our Privacy Policy",
                content:
                  "We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any significant changes by posting the updated policy on our website and updating the effective date at the top.",
              },
            ],
          };
          setPrivacyData(defaultData);
          await saveToFirebase(defaultData);
        }
      } catch (error) {
        console.error("Error fetching privacy policy data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyData();
  }, []);

  // Save data to Firebase
  const saveToFirebase = async (data = privacyData) => {
    try {
      const privacyRef = ref(db, "LayoutSettingData/PrivacyPolicy");
      await set(privacyRef, data);
      return true;
    } catch (error) {
      console.error("Error saving privacy policy data:", error);
      return false;
    }
  };

  // Handle editing introduction
  const handleEditIntro = () => {
    setEditIntroText(privacyData.introduction);
    setIsEditingIntro(true);
  };

  const handleSaveIntro = async () => {
    const updatedData = { ...privacyData, introduction: editIntroText };
    const success = await saveToFirebase(updatedData);
    if (success) {
      setPrivacyData(updatedData);
      setIsEditingIntro(false);
      toast({
        title: "Success",
        description: "Introduction updated successfully",
        showIcon: true,
        variant: "success",
      });
    }
  };

  // Handle editing section
  const handleEditSection = (index: number) => {
    const section = privacyData.sections[index];
    setEditingSectionIndex(index);
    setEditSectionTitle(section.title);
    setEditSectionContent(section.content);
    setEditingListItems(section.listItems || []);
  };

  const handleSaveSection = async () => {
    if (editingSectionIndex !== null) {
      const updatedSections = [...privacyData.sections];
      updatedSections[editingSectionIndex] = {
        title: editSectionTitle,
        content: editSectionContent,
        listItems: editingListItems.length > 0 ? editingListItems : undefined,
      };

      const updatedData = {
        ...privacyData,
        sections: updatedSections,
      };

      const success = await saveToFirebase(updatedData);
      if (success) {
        setPrivacyData(updatedData);
        setEditingSectionIndex(null);
        toast({
          title: "Success",
          description: "Section updated successfully",
          showIcon: true,
          variant: "success",
        });
      }
    }
  };

  // Handle adding new section
  const handleAddSection = () => {
    setEditSectionTitle("");
    setEditSectionContent("");
    setEditingListItems([]);
    setIsAddingSection(true);
  };

  const handleSaveNewSection = async () => {
    if (editSectionTitle.trim() === "" || editSectionContent.trim() === "") {
      toast({
        title: "Error",
        description: "Please enter both title and content for the new section",
        variant: "destructive",
        showIcon: true,
      });
      return;
    }

    const newSection: PrivacyPolicySection = {
      title: editSectionTitle,
      content: editSectionContent,
    };

    if (editingListItems.length > 0) {
      newSection.listItems = editingListItems;
    }

    const updatedData = {
      ...privacyData,
      sections: [...privacyData.sections, newSection],
    };

    const success = await saveToFirebase(updatedData);
    if (success) {
      setPrivacyData(updatedData);
      setIsAddingSection(false);
      toast({
        title: "Success",
        description: "New section added successfully",
        variant: "success",
        showIcon: true,
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to add new section",
        variant: "destructive",
        showIcon: true,
      });
    }
  };

  // Handle deleting section
  const handleDeleteSection = async (index: number) => {
    setSectionToDelete(index);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (sectionToDelete !== null) {
      const updatedSections = privacyData.sections.filter(
        (_, i) => i !== sectionToDelete
      );

      const updatedData = {
        ...privacyData,
        sections: updatedSections,
      };

      const success = await saveToFirebase(updatedData);
      if (success) {
        setPrivacyData(updatedData);
        toast({
          title: "Success",
          description: "Section deleted successfully",
          variant: "success",
          showIcon: true,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete section",
          variant: "destructive",
          showIcon: true,
        });
      }
    }
    setShowDeleteDialog(false);
    setSectionToDelete(null);
  };

  // Handle list items
  const handleAddListItem = () => {
    if (newListItem.trim()) {
      setEditingListItems([...editingListItems, newListItem]);
      setNewListItem("");
    }
  };

  const handleDeleteListItem = (index: number) => {
    const updatedListItems = editingListItems.filter((_, i) => i !== index);
    setEditingListItems(updatedListItems);
  };

  // Handle cancel button click
  const handleCancel = () => {
    setIsEditingIntro(false);
    setEditingSectionIndex(null);
    setIsAddingSection(false);
  };

  if (loading) {
    return <LayoutSkeleton />;
  }

  // Render introduction section
  const renderIntroduction = () => {
    return (
      <div className="mb-6">
        {isEditingIntro ? (
          <div className="space-y-4">
            <textarea
              value={editIntroText}
              onChange={(e) => setEditIntroText(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded min-h-[100px]"
            />
            <div className="flex space-x-2">
              <button
                onClick={handleSaveIntro}
                className="bg-customGreenColor text-white px-3 py-1 rounded flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-1" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-300 text-gray-700 px-3 py-1 rounded flex items-center"
              >
                <XIcon className="h-4 w-4 mr-1" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between">
            <p className="font-helvetica">{privacyData.introduction}</p>
            <button className="text-customGreenColor" onClick={handleEditIntro}>
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render section item
  const renderSection = (section: PrivacyPolicySection, index: number) => {
    return (
      <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
        {editingSectionIndex === index ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section Title
              </label>
              <input
                type="text"
                value={editSectionTitle}
                onChange={(e) => setEditSectionTitle(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section Content
              </label>
              <textarea
                value={editSectionContent}
                onChange={(e) => setEditSectionContent(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded min-h-[100px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                List Items (if applicable)
              </label>
              <div className="space-y-2">
                {editingListItems.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const updatedItems = [...editingListItems];
                        updatedItems[itemIndex] = e.target.value;
                        setEditingListItems(updatedItems);
                      }}
                      className="flex-1 p-2 border border-gray-300 rounded"
                    />
                    <button
                      className="ml-2 text-red-500"
                      onClick={() => handleDeleteListItem(itemIndex)}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center">
                  <input
                    type="text"
                    value={newListItem}
                    onChange={(e) => setNewListItem(e.target.value)}
                    placeholder="Add new list item"
                    className="flex-1 p-2 border border-gray-300 rounded"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddListItem();
                      }
                    }}
                  />
                  <button
                    className="ml-2 bg-gray-200 p-2 rounded"
                    onClick={handleAddListItem}
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleSaveSection}
                className="bg-customGreenColor text-white px-3 py-1 rounded flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-1" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-300 text-gray-700 px-3 py-1 rounded flex items-center"
              >
                <XIcon className="h-4 w-4 mr-1" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between">
              <h3 className="font-helveticaBold text-black text-lg">
                {section.title}
              </h3>
              <div className="flex space-x-2">
                <button
                  className="text-customGreenColor"
                  onClick={() => handleEditSection(index)}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  className="text-red-500"
                  onClick={() => handleDeleteSection(index)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <p className="mt-2 font-helvetica">{section.content}</p>
            {section.listItems && section.listItems.length > 0 && (
              <ul className="list-disc pl-5 mt-2">
                {section.listItems.map((item, itemIndex) => (
                  <li key={itemIndex} className="font-helvetica">
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-[1400px] mx-auto bg-white p-6">
      <div className="w-full flex flex-col font-normal text-sm space-y-6 text-black">
        <div className="flex gap-4 flex-col md:flex-row justify-between items-center w-full mb-8">
          <h1 className="font-helveticaBold text-3xl md:text-4xl text-black">
            Privacy Policy
          </h1>
          <button
            className="text-white w-full md:w-auto bg-customGreenColor justify-center hover:bg-opacity-90 transition-all px-4 py-2 rounded-md flex items-center"
            onClick={handleAddSection}
          >
            <PlusIcon className="h-4 w-4 mr-2" /> Add Section
          </button>
        </div>

        {/* Introduction section */}
        <div className="bg-gray-50 rounded-lg p-6">{renderIntroduction()}</div>

        {/* Add New Section Form */}
        {isAddingSection && (
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
            <h3 className="font-helveticaBold text-black text-lg mb-3">
              Add New Section
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Title
                </label>
                <input
                  type="text"
                  value={editSectionTitle}
                  onChange={(e) => setEditSectionTitle(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="Enter section title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Content
                </label>
                <textarea
                  value={editSectionContent}
                  onChange={(e) => setEditSectionContent(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded min-h-[100px]"
                  placeholder="Enter section content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List Items (if applicable)
                </label>
                <div className="space-y-2">
                  {editingListItems.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const updatedItems = [...editingListItems];
                          updatedItems[itemIndex] = e.target.value;
                          setEditingListItems(updatedItems);
                        }}
                        className="flex-1 p-2 border border-gray-300 rounded"
                      />
                      <button
                        className="ml-2 text-red-500"
                        onClick={() => handleDeleteListItem(itemIndex)}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={newListItem}
                      onChange={(e) => setNewListItem(e.target.value)}
                      placeholder="Add new list item"
                      className="flex-1 p-2 border border-gray-300 rounded"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleAddListItem();
                        }
                      }}
                    />
                    <button
                      className="ml-2 bg-gray-200 p-2 rounded"
                      onClick={handleAddListItem}
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveNewSection}
                  className="bg-customGreenColor text-white px-3 py-1 rounded flex items-center"
                >
                  <CheckIcon className="h-4 w-4 mr-1" /> Save
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-300 text-gray-700 px-3 py-1 rounded flex items-center"
                >
                  <XIcon className="h-4 w-4 mr-1" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sections List */}
        <div className="grid gap-6">
          {privacyData.sections.map((section, index) =>
            renderSection(section, index)
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <SortupDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSectionToDelete(null);
        }}
        header="Confirm Delete"
        footerButtons={[
          {
            name: "Delete",
            onClick: confirmDelete,
            disabled: false,
            className: "bg-customGreenColor text-white hover:bg-opacity-90",
          },
          {
            name: "Cancel",
            onClick: () => {
              setShowDeleteDialog(false);
              setSectionToDelete(null);
            },
            disabled: false,
            className: "bg-gray-200 text-gray-700 hover:bg-gray-300",
          },
        ]}
      >
        <p>
          Are you sure you want to delete this section? This action cannot be
          undone.
        </p>
      </SortupDialog>
    </div>
  );
};

export default EditPrivacyPolicy;
